import type {
  Actor,
  Message,
  Schema,
  Schemas,
  Snapshot,
  Target,
} from "./action.js";

/**
 * @packageDocumentation
 * @module act/types
 * @category Types
 * Application error type constants and error classes for the Act Framework.
 *
 * - `ERR_VALIDATION`: Schema validation error
 * - `ERR_INVARIANT`: Invariant validation error
 * - `ERR_CONCURRENCY`: Optimistic concurrency validation error on commits
 */
export const Errors = {
  ValidationError: "ERR_VALIDATION",
  InvariantError: "ERR_INVARIANT",
  ConcurrencyError: "ERR_CONCURRENCY",
  StreamClosedError: "ERR_STREAM_CLOSED",
  NonRetryableError: "ERR_NON_RETRYABLE",
  StoreError: "ERR_STORE",
} as const;

/**
 * Thrown when an action or event payload fails Zod schema validation.
 *
 * This error indicates that data doesn't match the expected schema defined
 * for an action or event. The `details` property contains the Zod validation
 * error with specific information about what failed.
 *
 * @example Catching validation errors
 * ```typescript
 * import { ValidationError } from "@rotorsoft/act";
 *
 * try {
 *   await app.do("createUser", target, {
 *     email: "invalid-email",  // Missing @ symbol
 *     age: -5                  // Negative age
 *   });
 * } catch (error) {
 *   if (error instanceof ValidationError) {
 *     console.error("Validation failed for:", error.target);
 *     console.error("Invalid payload:", error.payload);
 *     console.error("Validation details:", error.details);
 *     // details contains Zod error with field-level info
 *   }
 * }
 * ```
 *
 * @example Logging validation details
 * ```typescript
 * try {
 *   await app.do("updateProfile", target, payload);
 * } catch (error) {
 *   if (error instanceof ValidationError) {
 *     error.details.errors.forEach((err) => {
 *       console.error(`Field ${err.path.join(".")}: ${err.message}`);
 *     });
 *   }
 * }
 * ```
 *
 * @see {@link https://zod.dev | Zod documentation} for validation details
 */
export class ValidationError extends Error {
  /** The type of target being validated (e.g., "action", "event") */
  public readonly target: string;
  /** The invalid payload that failed validation */
  public readonly payload: any;
  /** Zod validation error details */
  public readonly details: any;

  constructor(target: string, payload: any, details: any) {
    super(`Invalid ${target} payload`);
    this.name = Errors.ValidationError;
    this.target = target;
    this.payload = payload;
    this.details = details;
  }
}

/**
 * Thrown when a business rule (invariant) is violated during action execution.
 *
 * Invariants are conditions that must hold true for an action to succeed.
 * They're checked after loading the current state but before emitting events.
 * This error provides complete context about what action was attempted and
 * why it was rejected.
 *
 * @template TState - State schema type
 * @template TEvents - Event schemas type
 * @template TActions - Action schemas type
 * @template TKey - Action name
 * @template TActor - Actor type extending base Actor
 *
 * @example Catching invariant violations
 * ```typescript
 * import { InvariantError } from "@rotorsoft/act";
 *
 * try {
 *   await app.do("withdraw",
 *     { stream: "account-123", actor: { id: "user1", name: "Alice" } },
 *     { amount: 1000 }
 *   );
 * } catch (error) {
 *   if (error instanceof InvariantError) {
 *     console.error("Action:", error.action);
 *     console.error("Reason:", error.description);
 *     console.error("Current state:", error.snapshot.state);
 *     console.error("Attempted payload:", error.payload);
 *   }
 * }
 * ```
 *
 * @example User-friendly error messages
 * ```typescript
 * try {
 *   await app.do("closeTicket", target, payload);
 * } catch (error) {
 *   if (error instanceof InvariantError) {
 *     // Present friendly message to user
 *     if (error.description === "Ticket must be open") {
 *       return { error: "This ticket is already closed" };
 *     } else if (error.description === "Not authorized") {
 *       return { error: "You don't have permission to close this ticket" };
 *     }
 *   }
 * }
 * ```
 *
 * @example Logging with context
 * ```typescript
 * try {
 *   await app.do("transfer", target, { to: "account2", amount: 500 });
 * } catch (error) {
 *   if (error instanceof InvariantError) {
 *     logger.error({
 *       action: error.action,
 *       stream: error.target.stream,
 *       actor: error.target.actor,
 *       reason: error.description,
 *       balance: error.snapshot.state.balance,
 *       attempted: error.payload.amount
 *     }, "Invariant violation");
 *   }
 * }
 * ```
 *
 * @see {@link Invariant} for defining business rules
 */
export class InvariantError<
  TState extends Schema,
  TEvents extends Schemas,
  TActions extends Schemas,
  TKey extends keyof TActions,
  TActor extends Actor = Actor,
> extends Error {
  /** The action that was attempted */
  readonly action: TKey;
  /** The action payload that was provided */
  readonly payload: Readonly<TActions[TKey]>;
  /** The target stream and actor context */
  readonly target: Target<TActor>;
  /** The current state snapshot when invariant was checked */
  readonly snapshot: Snapshot<TState, TEvents>;
  /** Human-readable description of why the invariant failed */
  readonly description: string;

  constructor(
    action: TKey,
    payload: Readonly<TActions[TKey]>,
    target: Target<TActor>,
    snapshot: Snapshot<TState, TEvents>,
    description: string
  ) {
    super(`${action as string} failed invariant: ${description}`);
    this.name = Errors.InvariantError;
    this.action = action;
    this.payload = payload;
    this.target = target;
    this.snapshot = snapshot;
    this.description = description;
  }
}

/**
 * Thrown when optimistic concurrency control detects a conflict.
 *
 * This error occurs when trying to commit events to a stream that has been
 * modified by another process since it was last loaded. The version number
 * doesn't match expectations, indicating a concurrent modification.
 *
 * This is a normal occurrence in distributed systems and should be handled
 * by reloading the current state and retrying the action.
 *
 * @example Handling concurrency conflicts with retry
 * ```typescript
 * import { ConcurrencyError } from "@rotorsoft/act";
 *
 * async function transferWithRetry(from, to, amount, maxRetries = 3) {
 *   for (let attempt = 0; attempt < maxRetries; attempt++) {
 *     try {
 *       await app.do("transfer",
 *         { stream: from, actor: currentUser },
 *         { to, amount }
 *       );
 *       return { success: true };
 *     } catch (error) {
 *       if (error instanceof ConcurrencyError) {
 *         if (attempt < maxRetries - 1) {
 *           console.log(`Concurrent modification detected, retrying... (${attempt + 1}/${maxRetries})`);
 *           await sleep(100 * Math.pow(2, attempt)); // Exponential backoff
 *           continue;
 *         }
 *       }
 *       throw error;
 *     }
 *   }
 *   return { success: false, reason: "Too many concurrent modifications" };
 * }
 * ```
 *
 * @example Logging concurrency conflicts
 * ```typescript
 * try {
 *   await app.do("updateInventory", target, payload);
 * } catch (error) {
 *   if (error instanceof ConcurrencyError) {
 *     logger.warn({
 *       stream: error.stream,
 *       expectedVersion: error.expectedVersion,
 *       actualVersion: error.lastVersion,
 *       events: error.events.map(e => e.name)
 *     }, "Concurrent modification detected");
 *   }
 * }
 * ```
 *
 * @example User feedback for conflicts
 * ```typescript
 * try {
 *   await app.do("editDocument", target, { content: newContent });
 * } catch (error) {
 *   if (error instanceof ConcurrencyError) {
 *     return {
 *       error: "This document was modified by another user. Please refresh and try again.",
 *       code: "CONCURRENT_MODIFICATION"
 *     };
 *   }
 * }
 * ```
 *
 * @see {@link Store.commit} for version checking details
 */
export class ConcurrencyError extends Error {
  /** The stream that had the concurrent modification */
  public readonly stream: string;
  /** The actual current version in the store */
  public readonly lastVersion: number;
  /** The events that were being committed */
  public readonly events: Message<Schemas, keyof Schemas>[];
  /** The version number that was expected */
  public readonly expectedVersion: number;

  constructor(
    stream: string,
    lastVersion: number,
    events: Message<Schemas, keyof Schemas>[],
    expectedVersion: number
  ) {
    // Message lists stream + event names only. Payloads remain accessible
    // via `error.events` for callers who need them — keeping them out of
    // the message avoids MB-scale strings on contended writes and keeps
    // potentially-sensitive data out of log streams.
    super(
      `Concurrency error committing "${events
        .map((e) => `${stream}.${e.name}`)
        .join(
          ", "
        )}". Expected version ${expectedVersion} but found version ${lastVersion}.`
    );
    this.name = Errors.ConcurrencyError;
    this.stream = stream;
    this.lastVersion = lastVersion;
    this.events = events;
    this.expectedVersion = expectedVersion;
  }
}

/**
 * Thrown when attempting to write to a stream that has been closed
 * with a tombstone event.
 *
 * A tombstoned stream is permanently closed — no further actions can
 * be executed against it. The only way to reopen a tombstoned stream
 * is through `Act.close()` with a `restart` callback.
 *
 * @example
 * ```typescript
 * import { StreamClosedError } from "@rotorsoft/act";
 *
 * try {
 *   await app.do("updateTicket", target, payload);
 * } catch (error) {
 *   if (error instanceof StreamClosedError) {
 *     console.error(`Stream ${error.stream} is closed`);
 *   }
 * }
 * ```
 *
 * @see {@link Act.close} for closing streams
 */
export class StreamClosedError extends Error {
  /** The stream that is closed */
  public readonly stream: string;

  constructor(stream: string) {
    super(`Stream "${stream}" is closed (tombstoned)`);
    this.name = Errors.StreamClosedError;
    this.stream = stream;
  }
}

/**
 * Thrown by a {@link Store} adapter when an infrastructure operation fails
 * for a reason that is *not* a domain condition — a dropped connection, a
 * transaction rollback, a query timeout. It is the typed boundary between
 * "the store is unavailable/degraded" and the domain errors above
 * ({@link ConcurrencyError}, {@link StreamClosedError}), which describe
 * legitimate outcomes the caller should branch on.
 *
 * Adapters wrap their driver errors in `StoreError` (preserving the
 * original via `cause`) so the orchestrator can distinguish a degraded
 * backend from "no work" and react accordingly — see the drain circuit
 * breaker, which trips on repeated `StoreError`s and surfaces an
 * `error` lifecycle event instead of silently spinning on a down
 * database.
 *
 * @example
 * ```typescript
 * app.on("error", ({ error, circuit }) => {
 *   if (error instanceof StoreError)
 *     alert(`store ${error.operation} failing; circuit=${circuit}`);
 * });
 * ```
 */
export class StoreError extends Error {
  /** The store operation that failed (e.g. `"claim"`, `"ack"`, `"commit"`). */
  public readonly operation: string;

  constructor(operation: string, options?: { cause?: unknown }) {
    super(`Store operation "${operation}" failed`, options);
    this.name = Errors.StoreError;
    this.operation = operation;
  }
}

/**
 * Thrown by a reaction handler to signal that the failure is permanent
 * and the drain pipeline should block the stream immediately, without
 * consuming the rest of the `maxRetries` budget.
 *
 * The drain finalizer detects `instanceof NonRetryableError` and forces
 * `block = options.blockOnError` regardless of `lease.retry`. When
 * `blockOnError` is `false`, behavior is unchanged (drain keeps retrying
 * forever) — the class never overrides the operator's explicit "never
 * block" choice.
 *
 * Use this for failures the handler *knows* won't get better on retry:
 * a 4xx from a webhook, a `ZodError` on malformed input, a "user
 * deleted" 404 from a downstream API. Use regular `Error` (or a
 * subclass) for transient failures so the existing retry-with-backoff
 * loop applies.
 *
 * @example Wrapping a permanent downstream error
 * ```typescript
 * import { NonRetryableError } from "@rotorsoft/act";
 *
 * .on("OrderConfirmed")
 *   .do(async (event) => {
 *     const res = await fetch(url, ...);
 *     if (res.status >= 400 && res.status < 500) {
 *       throw new NonRetryableError(
 *         `webhook ${url} responded ${res.status}`,
 *         { cause: await res.text() }
 *       );
 *     }
 *     if (!res.ok) throw new Error(`webhook ${url} responded ${res.status}`);
 *   })
 * ```
 *
 * @example Marking validation failures as non-retryable
 * ```typescript
 * .on("PaymentReceived")
 *   .do(async (event) => {
 *     const parsed = Schema.safeParse(event.data);
 *     if (!parsed.success) {
 *       throw new NonRetryableError("payment payload failed validation", {
 *         cause: parsed.error,
 *       });
 *     }
 *     // ... handle parsed payload
 *   })
 * ```
 */
export class NonRetryableError extends Error {
  /** The original failure, if any. Mirrors the standard `Error.cause` shape. */
  public override readonly cause?: unknown;

  constructor(message: string, options?: { cause?: unknown }) {
    super(message);
    this.name = Errors.NonRetryableError;
    this.cause = options?.cause;
  }
}
